import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import { homeData } from '@/mocks/data';

export default function AboutPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get('phone_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus('success');
      form.reset();
      setTimeout(() => setFormStatus('idle'), 3000);
      return;
    }

    formData.delete('phone_alt');

    setFormStatus('loading');
    setFormError('');

    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        params.append(key, value.toString());
      });

      const response = await fetch('https://readdy.ai/api/form/d9sn63p4ne17ei0sdkc0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      const code = parsed?.code;
      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

      if (response.ok && code === 'OK') {
        setFormStatus('success');
        form.reset();
        setTimeout(() => setFormStatus('idle'), 3000);
      } else {
        setFormStatus('error');
        setFormError(serverMsg || '提交失败，请稍后重试');
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查网络连接后重试');
    }
  };

  const organizationJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '男同网站',
    url: 'https://www.nantongwangzhan.com',
    description: '专为男同群体打造的安全、友好的线上社区平台，提供交友、资讯、活动与支持服务。',
    foundingDate: '2020',
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '400-888-9999',
      contactType: 'customer service',
      email: 'contact@nantongwangzhan.com',
      availableLanguage: 'Chinese',
    },
    address: {
      '@type': 'PostalAddress',
      streetAddress: '朝阳区彩虹大厦18层',
      addressLocality: '北京',
      addressRegion: '北京',
      postalCode: '100020',
      addressCountry: 'CN',
    },
    sameAs: [
      'https://weibo.com/nantongwangzhan',
      'https://weixin.qq.com/nantongwangzhan',
    ],
  };

  const webpageJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于我们 - 男同网站',
    description: '了解男同网站的使命、团队和联系方式。我们致力于为男同群体打造安全、温暖的线上社区。',
    url: 'https://www.nantongwangzhan.com/about',
    isPartOf: {
      '@type': 'WebSite',
      name: '男同网站',
      url: 'https://www.nantongwangzhan.com',
    },
  };

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: homeData.faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  const teamMembers = [
    {
      name: '李晨曦',
      role: '创始人 & 首席执行官',
      bio: 'LGBTQ+权益倡导者，致力于为社群创造安全包容的线上空间。',
      image: 'https://readdy.ai/api/search-image?query=Professional%20portrait%20of%20Asian%20man%20in%20modern%20office%2C%20warm%20lighting%2C%20friendly%20confident%20expression%2C%20clean%20background%2C%20professional%20headshot%2C%20terracotta%20warm%20tones&width=300&height=300&seq=team-member-1&orientation=squarish',
    },
    {
      name: '王心语',
      role: '社区运营总监',
      bio: '十年社群运营经验，深信每个人都有权被看见、被听见。',
      image: 'https://readdy.ai/api/search-image?query=Professional%20portrait%20of%20Asian%20person%20in%20creative%20workspace%2C%20warm%20natural%20light%2C%20friendly%20approachable%20expression%2C%20clean%20modern%20background%2C%20professional%20headshot%2C%20amber%20warm%20tones&width=300&height=300&seq=team-member-2&orientation=squarish',
    },
    {
      name: '张阳光',
      role: '内容主编',
      bio: '资深媒体人，专注LGBTQ+议题报道，用文字传递温暖和力量。',
      image: 'https://readdy.ai/api/search-image?query=Professional%20portrait%20of%20Asian%20man%20in%20cozy%20cafe%20setting%2C%20warm%20afternoon%20light%2C%20thoughtful%20expression%2C%20clean%20background%2C%20professional%20headshot%2C%20sage%20green%20warm%20tones&width=300&height=300&seq=team-member-3&orientation=squarish',
    },
    {
      name: '陈康康',
      role: '健康顾问',
      bio: '执业医师，专注于LGBTQ+群体健康科普，提供科学专业的健康指导。',
      image: 'https://readdy.ai/api/search-image?query=Professional%20portrait%20of%20Asian%20person%20in%20medical%20setting%2C%20warm%20professional%20lighting%2C%20compassionate%20expression%2C%20clean%20clinic%20background%2C%20professional%20headshot%2C%20warm%20cream%20tones&width=300&height=300&seq=team-member-4&orientation=squarish',
    },
  ];

  const milestones = [
    { year: '2020', title: '平台成立', description: '男同网站正式上线，首批1000名用户加入社群。' },
    { year: '2021', title: '快速发展', description: '用户突破10000人，上线社区活动功能。' },
    { year: '2022', title: '内容升级', description: '引入专业编辑团队，推出高质量资讯内容。' },
    { year: '2023', title: '健康科普', description: '与专业医疗机构合作，推出健康科普板块。' },
    { year: '2024', title: '社群突破', description: '注册用户突破50000人，覆盖全国200+城市。' },
    { year: '2025', title: '服务升级', description: '上线心理支持服务，为社群提供专业心理健康资源。' },
  ];

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(organizationJsonLd)}</script>
      <script type="application/ld+json">{JSON.stringify(webpageJsonLd)}</script>
      <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <section className="bg-background-100 border-b border-background-200/70">
            <div className="max-w-7xl mx-auto px-4 md:px-6 py-14 md:py-20">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: '关于我们' },
                ]}
              />
              <div className="max-w-3xl mt-6">
                <h1 className="text-4xl md:text-5xl font-heading font-bold text-foreground-950 mb-6">
                  关于我们
                </h1>
                <p className="text-lg text-foreground-600 leading-relaxed">
                  男同网站成立于2020年，是中国领先的男同社区与交友平台。我们的使命是为男同群体创造一个安全、温暖、真实的线上空间，让每一个人都能在这里找到归属感。
                </p>
              </div>
            </div>
          </section>

          <section className="py-16 md:py-20">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                <div>
                  <h2 className="text-3xl font-heading font-bold text-foreground-950 mb-5">
                    我们的使命
                  </h2>
                  <div className="space-y-4 text-foreground-600 leading-relaxed">
                    <p>
                      在当今社会，男同群体仍然面临着诸多挑战——从自我认同的困惑到社会偏见的压力。我们深知，一个安全、包容的社群空间对于每个人的成长和幸福至关重要。
                    </p>
                    <p>
                      男同网站正是在这样的信念下诞生的。我们不只是一个交友平台，更是一个让男同群体能够自由表达、相互支持、共同成长的温暖家园。
                    </p>
                    <p>
                      我们相信，<strong>爱就是爱，每个人都值得被尊重、被接纳、被爱</strong>。无论你处于人生的哪个阶段，无论你正在经历什么，这里都有一群人愿意倾听、理解并支持你。
                    </p>
                  </div>
                </div>
                <div className="rounded-lg overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Warm%20diverse%20community%20gathering%20in%20modern%20cozy%20space%2C%20people%20connecting%20and%20supporting%20each%20other%2C%20rainbow%20colors%20subtly%20integrated%2C%20warm%20natural%20light%2C%20inclusive%20atmosphere%2C%20editorial%20lifestyle%20photography%2C%20terracotta%20and%20amber%20tones%2C%20harmonious%20composition&width=700&height=500&seq=about-mission&orientation=landscape"
                    alt="我们的使命"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </section>

          <section className="py-16 md:py-20 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 mb-4">
                  我们的价值观
                </h2>
                <p className="text-foreground-600 text-base max-w-xl mx-auto">
                  这些核心价值观指引着我们每一天的工作和决策
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {homeData.aboutHighlights.map((item, index) => (
                  <div
                    key={index}
                    className="bg-background-50 rounded-lg border border-background-200/70 p-6 text-center"
                  >
                    <div className="w-14 h-14 bg-accent-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <i className={`${item.icon} text-2xl text-accent-600`}></i>
                    </div>
                    <h3 className="text-lg font-semibold text-foreground-950 mb-2">
                      {item.title}
                    </h3>
                    <p className="text-sm text-foreground-600">{item.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="py-16 md:py-20">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 mb-4">
                  发展历程
                </h2>
                <p className="text-foreground-600 text-base max-w-xl mx-auto">
                  从零开始的每一步，都离不开社群的支持与陪伴
                </p>
              </div>

              <div className="relative max-w-3xl mx-auto">
                <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-background-300 transform md:-translate-x-px"></div>

                <div className="space-y-8">
                  {milestones.map((item, index) => (
                    <div
                      key={index}
                      className={`relative flex items-start gap-6 ${
                        index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                      }`}
                    >
                      <div className="hidden md:block flex-1"></div>
                      <div className="absolute left-0 md:left-1/2 w-8 h-8 bg-accent-500 rounded-full border-4 border-background-50 transform -translate-x-1/2 flex items-center justify-center z-10">
                        <div className="w-2 h-2 bg-background-50 rounded-full"></div>
                      </div>
                      <div className="flex-1 pl-12 md:pl-0 md:px-8">
                        <div className="bg-background-50 rounded-lg border border-background-200/70 p-5">
                          <span className="inline-block px-3 py-1 bg-accent-100 text-accent-700 text-xs font-semibold rounded-full mb-2">
                            {item.year}
                          </span>
                          <h3 className="text-lg font-semibold text-foreground-950 mb-1">
                            {item.title}
                          </h3>
                          <p className="text-sm text-foreground-600">{item.description}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          <section className="py-16 md:py-20 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 mb-4">
                  核心团队
                </h2>
                <p className="text-foreground-600 text-base max-w-xl mx-auto">
                  一群充满热情的人，致力于让社群变得更美好
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {teamMembers.map((member, index) => (
                  <div
                    key={index}
                    className="bg-background-50 rounded-lg border border-background-200/70 overflow-hidden text-center group"
                  >
                    <div className="h-56 overflow-hidden">
                      <img
                        src={member.image}
                        alt={member.name}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="p-5">
                      <h3 className="text-lg font-semibold text-foreground-950 mb-1">
                        {member.name}
                      </h3>
                      <p className="text-sm text-primary-600 font-medium mb-3">{member.role}</p>
                      <p className="text-sm text-foreground-600 leading-relaxed">{member.bio}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="py-16 md:py-20">
            <div className="max-w-3xl mx-auto px-4 md:px-6">
              <div className="text-center mb-10">
                <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 mb-4">
                  常见问题
                </h2>
              </div>

              <div className="space-y-3">
                {homeData.faqs.map((faq, index) => (
                  <div
                    key={index}
                    className="bg-background-50 rounded-lg border border-background-200/70 overflow-hidden"
                  >
                    <button
                      className="w-full flex items-center justify-between p-5 text-left cursor-pointer"
                      onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    >
                      <h3 className="text-base font-semibold text-foreground-950 pr-4">
                        {faq.question}
                      </h3>
                      <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                        <i
                          className={`text-lg text-foreground-500 transition-transform duration-300 ${
                            openFaq === index ? 'ri-subtract-line' : 'ri-add-line'
                          }`}
                        ></i>
                      </div>
                    </button>
                    <div
                      className={`transition-all duration-300 overflow-hidden ${
                        openFaq === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                      }`}
                    >
                      <div className="px-5 pb-5 text-foreground-600 text-sm leading-relaxed">
                        {faq.answer}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="py-16 md:py-20 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                <div>
                  <h2 className="text-3xl font-heading font-bold text-foreground-950 mb-5">
                    联系我们
                  </h2>
                  <p className="text-foreground-600 mb-8 leading-relaxed">
                    有任何问题、建议或合作意向？我们随时欢迎你的来信。你的每一条反馈都是我们前进的动力。
                  </p>

                  <div className="space-y-5">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <i className="ri-mail-line text-lg text-primary-600"></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-foreground-950 mb-1">电子邮箱</h4>
                        <p className="text-foreground-600 text-sm">contact@nantongwangzhan.com</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <i className="ri-phone-line text-lg text-primary-600"></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-foreground-950 mb-1">客服电话</h4>
                        <p className="text-foreground-600 text-sm">400-888-9999</p>
                        <p className="text-foreground-500 text-xs mt-0.5">周一至周五 9:00-18:00</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <i className="ri-map-pin-line text-lg text-primary-600"></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-foreground-950 mb-1">办公地址</h4>
                        <p className="text-foreground-600 text-sm">北京市朝阳区彩虹大厦18层</p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-8">
                    <h4 className="font-semibold text-foreground-950 mb-3">关注我们</h4>
                    <div className="flex items-center gap-3">
                      <a href="#" className="w-10 h-10 rounded-full bg-background-50 border border-background-200/70 flex items-center justify-center hover:bg-primary-500 hover:text-background-50 hover:border-primary-500 transition-all cursor-pointer" aria-label="微信">
                        <i className="ri-wechat-line text-lg"></i>
                      </a>
                      <a href="#" className="w-10 h-10 rounded-full bg-background-50 border border-background-200/70 flex items-center justify-center hover:bg-primary-500 hover:text-background-50 hover:border-primary-500 transition-all cursor-pointer" aria-label="微博">
                        <i className="ri-weibo-line text-lg"></i>
                      </a>
                      <a href="#" className="w-10 h-10 rounded-full bg-background-50 border border-background-200/70 flex items-center justify-center hover:bg-primary-500 hover:text-background-50 hover:border-primary-500 transition-all cursor-pointer" aria-label="小红书">
                        <i className="ri-reddit-line text-lg"></i>
                      </a>
                    </div>
                  </div>
                </div>

                <div className="bg-background-50 rounded-lg border border-background-200/70 p-6 md:p-8">
                  <h3 className="text-xl font-heading font-bold text-foreground-950 mb-6">
                    发送消息
                  </h3>
                  <form data-readdy-form="true" onSubmit={handleSubmit} className="space-y-4">
                    <input
                      type="text"
                      name="phone_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="fixed opacity-0 pointer-events-none"
                    />
                    {formStatus === 'success' && (
                      <div className="p-3 bg-accent-100 text-accent-700 rounded-lg text-sm">
                        消息已成功发送，我们会尽快回复你！
                      </div>
                    )}
                    {formStatus === 'error' && (
                      <div className="p-3 bg-primary-100 text-primary-700 rounded-lg text-sm">
                        {formError}
                      </div>
                    )}
                    <div>
                      <label htmlFor="about-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        姓名
                      </label>
                      <input
                        type="text"
                        id="about-name"
                        name="name"
                        className="w-full px-4 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入你的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="about-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        邮箱
                      </label>
                      <input
                        type="email"
                        id="about-email"
                        name="email"
                        className="w-full px-4 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入你的邮箱"
                      />
                    </div>
                    <div>
                      <label htmlFor="about-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        留言
                      </label>
                      <textarea
                        id="about-message"
                        name="message"
                        rows={4}
                        maxLength={500}
                        className="w-full px-4 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                        placeholder="请输入你的留言（500字以内）"
                      ></textarea>
                    </div>
                    <button
                      type="submit"
                      disabled={formStatus === 'loading'}
                      className="w-full px-6 py-3 bg-primary-500 text-background-50 rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {formStatus === 'loading' ? '发送中...' : '发送消息'}
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}